import React, { useState, useEffect, useCallback } from 'react';
import {
    Button,
    Card,
    Row,
    Col,
    Tag,
    Input,
    Checkbox,
    Space,
    Divider,
    message,
    Dropdown,
    Menu,
    Modal,
    Empty,
    Tooltip,
} from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { glossaryFilters } from '../../containers/profile/FilterOptions/FilterOptions';
import { ReactComponent as MoreVertIcon } from '../../../../../images/more-vert.svg';
import FilterDropdown from '../../components/FilterDropdown/FilterDropdown';
import EditTagTermsModal from '../../../../shared/tags/AddTagsTermsModal';
import { BulkAction } from '../../../../shared/bulkAction/BulkAction';
import { AutoTooltip } from '../../components/common/AutoTooltip';
import { StyledTable } from '../../components/styled/StyledTable';
import CustomAvatar from '../../../../shared/avatar/CustomAvatar';
import CustomTooltip from '../../components/common/CustomTooltip';
import MetaDataPopup, { getStatusClass } from './MetaDataPopup';
import { PaginationBar } from './PaginationBar';

import { useBatchRemoveTermsMutation } from '../../../../../graphql/mutations.generated';
import { useGetEntitiesLazyQuery } from '../../../../../graphql/entity.generated';
import { useEntityRegistry } from '../../../../useEntityRegistry';
import { useEntityData, useRefetch, useGlossaryTermRefetch, useGlossaryTermData } from '../../EntityContext';
import { useRbac } from '../../useRbac';
import { ENTITY_ACTIONS } from '../../rbacConstantsService';
import { ComponentSpinner } from '../../../../shared/ComponentSpinner';
import { useTranslation } from '../../../../../i18n/useTranslation';
import { handleBatchError } from '../../utils';
import { EntityType } from '../../../../../types.generated';

const ActionButtonContainer = styled.div`
    display: flex;
    justify-content: right;
`;

const StyledMenu = styled(Menu)`
    min-width: 148px !important;
    border-radius: 4px;
    padding: 0;

    .ant-dropdown-menu-item {
        color: #4c4e54;
        padding: 8px 12px !important;
    }
`;

/**
 * Process glossary terms to handle multiple boolean values in the isSystem array
 * @param glossaryTerms - Array of glossary terms
 * @returns Array of processed glossary terms
 */
function processGlossaryTerms(glossaryTerms) {
    return glossaryTerms.flatMap((term) => {
        const isSystemBooleans = term.isSystem?.filter((item): item is boolean => typeof item === 'boolean') || [];
        if (isSystemBooleans.length > 1) {
            return [
                { ...term, isSystem: [true] },
                { ...term, isSystem: [false] },
            ];
        }
        return [term];
    });
}

export const GlossaryTermsTab = () => {
    const { t } = useTranslation();
    const { entityData, entityType } = useEntityData();
    const { canPerformEntityAction } = useRbac();
    const { GLOSSARY_TERMS } = ENTITY_ACTIONS;

    const canEditGlossaryTerms = canPerformEntityAction(entityType, GLOSSARY_TERMS);
    const noPermissionTooltip = !canEditGlossaryTerms ? t('UNAUTHORISED_TOOLTIP') : undefined;
    // Only disable when explicitly set to true (null and false are treated as enabled)
    const isReadOnly = entityData?.readOnly?.readOnly === true;
    const isEditDisabled = isReadOnly || !canEditGlossaryTerms;
    const editDisabledTooltip = noPermissionTooltip ?? (isReadOnly ? t('ASSET_MARKED_AS_READ_ONLY') : undefined);

    const entityRegistry = useEntityRegistry();
    const fetchMore = useGlossaryTermRefetch();
    const refetch = useRefetch();

    const { glossaryTermsData: data, isGlossaryTermsLoading: loading } = useGlossaryTermData();
    const [urnMapper, setUrnMapper] = useState<Record<string, string>>({});
    const [emailMapper, setEmailMapper] = useState<Record<string, string>>({});
    const [selectedItems, setSelectedItems]: any = useState([]);
    const [filterOptions, setFilterOptions]: any = useState([]);
    const [glossaryTerms, setGlossaryTerms]: any = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);

    const defaultInput = {
        start: 0,
        count: 10000,
        urn: entityData?.urn,
    };

    const [getEntities, { data: entityDetails, loading: isEntityLoading }] = useGetEntitiesLazyQuery();
    const [batchRemoveTermsMutation] = useBatchRemoveTermsMutation();

    const getFilteredGlossaryTerms = useCallback(
        (searchValue) => {
            return data?.getGlossaryTabDetails?.terms?.filter((term) => {
                const termName = term?.name.toLowerCase() ?? '';

                // Ensure all filter conditions are met
                const passesFilters = filterOptions.every(
                    (filter) => term?.[filter.field]?.includes(filter.values[0]), // Consider all values if needed
                );

                return termName.includes(searchValue.toLowerCase()) && passesFilters;
            });
        },
        [data?.getGlossaryTabDetails?.terms, filterOptions],
    );

    useEffect(() => {
        // Fetches the entities for the glossary terms
        const fetchEntities = (terms) => {
            const ownerURNs = terms?.reduce((acc, current) => (current.owners ? acc.concat(current.owners) : acc), []);
            const domainURNs = terms?.flatMap((item) => item.domains);
            const dataProductURNs = terms?.flatMap((item) => item.dataProducts);
            const glossaryTermURNs = terms?.map((item) => item.urn);

            getEntities({
                variables: {
                    urns: [...domainURNs, ...dataProductURNs, ...ownerURNs, ...glossaryTermURNs],
                },
            });
        };

        if (data?.getGlossaryTabDetails?.terms?.length) {
            const terms = data?.getGlossaryTabDetails?.terms;

            // Only fetch entities when terms data changes
            fetchEntities(terms);
        }
    }, [data?.getGlossaryTabDetails?.terms, getEntities]);

    // Separate useEffect for filtering and updating the UI state
    useEffect(() => {
        if (data?.getGlossaryTabDetails?.terms?.length) {
            // Apply filtering and processing in one go
            const filteredTerms = getFilteredGlossaryTerms(searchText);
            const processedTerms = processGlossaryTerms(filteredTerms);
            setGlossaryTerms(processedTerms);
        } else {
            setGlossaryTerms([]);
        }
    }, [data?.getGlossaryTabDetails?.terms, searchText, filterOptions, getFilteredGlossaryTerms]);

    // Fetches the glossary terms on refetch
    const generateDataGrid = () => {
        return (glossaryTerms || [])
            .map((term: any) => {
                return {
                    ...term,
                    key: `${term.urn}-${term.isSystem?.[0]}`,
                    subType: term.subTypes?.[0] || '-',
                };
            })
            .slice((page - 1) * pageSize, page * pageSize);
    };

    // Fetches the glossary terms
    const refetchGridListing = () => {
        setTimeout(() => {
            fetchMore?.({
                variables: {
                    input: defaultInput,
                },
            });
            refetch();
        }, 2000);
    };

    const getEntityDetailsByType = (entity) => {
        const displayNameResolvers = {
            [EntityType.CorpUser]: entity.properties?.fullName || 'System',
            [EntityType.GlossaryTerm]: entity?.parentNodes?.nodes,
        };

        return displayNameResolvers[entity.type] || entity.properties?.name || entity.properties?.fullName || '';
    };

    useEffect(() => {
        if (!entityDetails?.entities || entityDetails.entities.length === 0) return;

        const newUrnMapper: Record<string, string> = {};
        const newEmailMapper: Record<string, string> = {};

        entityDetails.entities.forEach((entity: any) => {
            newUrnMapper[entity.urn] = getEntityDetailsByType(entity);
            if (entity.type === EntityType.CorpUser && entity.properties?.email) {
                newEmailMapper[entity.urn] = entity.properties.email;
            }
        });

        setUrnMapper((prev) => ({ ...prev, ...newUrnMapper }));
        setEmailMapper((prev) => ({ ...prev, ...newEmailMapper }));
    }, [entityDetails?.entities]);

    // Utility function to check if a term is a system term
    const isSystemTerm = (term) => !!term?.isSystem?.[0];

    // Utility function to get a term by URN
    const getTermByUrn = (urn) => glossaryTerms.find((term) => term.urn === urn && !isSystemTerm(term));

    const handleClick = (record) => {
        // Prevent selection of system terms
        if (isSystemTerm(record)) {
            return;
        }

        setSelectedItems((prevSelected) =>
            prevSelected.includes(record.urn)
                ? prevSelected.filter((urn) => urn !== record.urn)
                : [...prevSelected, record.urn],
        );
    };

    const handleSelectAll = () => {
        // Filter out system terms from current page
        const nonSystemTermsInCurrentPage = generateDataGrid().filter((term) => !isSystemTerm(term));

        // Get URNs for the current page non-system terms
        const currentPageUrns = nonSystemTermsInCurrentPage.map((term) => term.urn);

        // Check if all current page non-system terms are already selected
        const allCurrentPageTermsSelected =
            currentPageUrns.length > 0 && currentPageUrns.every((urn) => selectedItems.includes(urn));

        if (allCurrentPageTermsSelected) {
            // If all current page terms are selected, deselect them
            setSelectedItems(selectedItems.filter((urn) => !currentPageUrns.includes(urn)));
        } else {
            // Get selections from other pages (items that aren't on current page)
            const otherPagesUrns = selectedItems.filter((urn) => !currentPageUrns.includes(urn));
            // Add current page terms to the selection
            setSelectedItems([...otherPagesUrns, ...currentPageUrns]);
        }
    };

    const batchRemoveTerms = (urn = '') => {
        const msg = urn ? t('Term') : t('Terms');

        // For batch operations, filter out any system terms that might have been selected
        const termUrnsToRemove = urn ? [urn] : selectedItems.filter((selectedUrn) => getTermByUrn(selectedUrn));

        // If no terms to remove after filtering, return early
        if (termUrnsToRemove.length === 0) {
            return;
        }

        Modal.confirm({
            title: t('Do you want to remove {{msg}}?', { msg }),
            content: t('Are you sure you want to remove {{msg}}?', { msg }),
            onOk() {
                batchRemoveTermsMutation({
                    variables: {
                        input: {
                            termUrns: termUrnsToRemove,
                            resources: [
                                {
                                    resourceUrn: entityData?.urn || '',
                                },
                            ],
                        },
                    },
                })
                    .then(({ errors }) => {
                        if (!errors) {
                            setPage(1);
                            message.success({
                                content: t('Removed {{msg}} Successfully', { msg }),
                                duration: 2,
                            });
                        }
                    })
                    .catch((e) => {
                        message.destroy();
                        message.error(
                            handleBatchError(selectedItems, e, {
                                content: t('Failed to remove: \n {{message}}', { message: e.message || '' }),
                                duration: 3,
                            }),
                        );
                    })
                    .finally(() => {
                        refetchGridListing();
                        setSelectedItems([]);
                    });
            },
            onCancel() {},
            okText: t('Yes'),
            className: 'f-borderless-modal',
            okButtonProps: {
                type: undefined,
                danger: true,
            },
            autoFocusButton: null,
            maskClosable: true,
            centered: true,
        });
    };

    const removeRelationship = (urn) => {
        // Find the term with the given URN
        const foundTerm = getTermByUrn(urn);
        // Skip removal if it's a system term
        if (!foundTerm) {
            return;
        }

        batchRemoveTerms(urn);
    };

    const glossaryGird = [
        {
            width: '4%',
            title: (() => {
                // Get current page data and filter out system terms
                const currentPageTerms = generateDataGrid();
                const currentPageNonSystemTerms = currentPageTerms.filter((term) => !isSystemTerm(term));
                const currentPageUrns = currentPageNonSystemTerms.map((term) => term.urn);

                // Determine if all/some terms on current page are selected
                const allPageTermsSelected =
                    currentPageUrns.length > 0 && currentPageUrns.every((urn) => selectedItems.includes(urn));

                const somePageTermsSelected =
                    !allPageTermsSelected && currentPageUrns.some((urn) => selectedItems.includes(urn));

                return (
                    <Tooltip title={editDisabledTooltip} overlayClassName="f-tooltip-white">
                        <Checkbox
                            aria-label={t('Select All')}
                            onClick={handleSelectAll}
                            disabled={!glossaryTerms.length || isEditDisabled}
                            checked={allPageTermsSelected}
                            indeterminate={somePageTermsSelected}
                        />
                    </Tooltip>
                );
            })(),
            ariaLabel: t('Select All'),
            dataIndex: 'selected',
            key: 'selected',
            render: (_, record) => {
                const checked = !isSystemTerm(record) && selectedItems.includes(record?.urn);
                const isDisabled = isSystemTerm(record) || isEditDisabled;

                return (
                    <Tooltip title={editDisabledTooltip} overlayClassName="f-tooltip-white">
                        <Checkbox
                            className="ml-2"
                            aria-label={t('Select this Row')}
                            checked={checked}
                            onClick={() => handleClick(record)}
                            disabled={isDisabled}
                        />
                    </Tooltip>
                );
            },
        },
        {
            width: '35%',
            title: t('Term Name'),
            ariaLabel: t('Term Name'),
            dataIndex: 'name',
            key: 'name',
            ellipsis: {
                showTitle: false,
            },
            render: (_, record) => {
                const breadcrumb: any = urnMapper[record.urn] || [];
                const themeColor = getStatusClass(record.governanceStatus);
                const url = entityRegistry.getEntityUrl(EntityType.GlossaryTerm, record.urn);

                return (
                    <>
                        <Row gutter={8} align="middle" className="mx-0">
                            <Col flex="none">
                                <Link
                                    className="f-text-medium-content f-text-single-line f-text-semibold f-color-dark-black-s100"
                                    role="button"
                                    tabIndex={0}
                                    to={url}
                                >
                                    <AutoTooltip>{record?.name}</AutoTooltip>
                                </Link>
                            </Col>
                            {record.governanceStatus && (
                                <Col flex="none">
                                    <Tag className={`f-small label-tag ${themeColor} ma-0`}>
                                        <span title={record.governanceStatus?.replace(/_/g, ' ')}>
                                            {record.governanceStatus?.replace(/_/g, ' ')}
                                        </span>
                                    </Tag>
                                </Col>
                            )}
                        </Row>
                        <div className="f-text-medium-content f-color-dark-black-s70">
                            {breadcrumb.map((item) => `${item.properties.name} / `)}
                        </div>
                    </>
                );
            },
        },
        {
            width: '15%',
            title: t('SubType'),
            ariaLabel: t('SubType'),
            dataIndex: 'subType',
            key: 'subType',
            ellipsis: {
                showTitle: false,
            },
            render: (subType) => <AutoTooltip>{subType}</AutoTooltip>,
        },
        {
            width: '15%',
            title: t('Domain'),
            ariaLabel: t('Domain'),
            dataIndex: 'domain',
            key: 'domain',
            ellipsis: {
                showTitle: false,
            },
            render: (_, record) => <AutoTooltip>{urnMapper[record.domains[0]] || '-'}</AutoTooltip>,
        },
        {
            width: '15%',
            title: t('Data Product'),
            ariaLabel: t('Data Product'),
            dataIndex: 'dataProduct',
            key: 'dataProduct',
            render: (_, record) => {
                const dataProductUrn = record?.dataProducts?.[0];

                return (
                    <Row wrap={false} gutter={4} align="middle" className="mx-0">
                        <Col flex="none">
                            {urnMapper[dataProductUrn] ? (
                                <Tag className="label-tag grey-tag-light ma-0">
                                    <span title={urnMapper[dataProductUrn]}>{urnMapper[dataProductUrn]}</span>
                                </Tag>
                            ) : (
                                <span>-</span>
                            )}
                        </Col>
                        {record?.dataProducts.length > 1 && (
                            <Col flex="none">
                                <CustomTooltip items={record?.dataProducts} itemMapper={urnMapper} />
                            </Col>
                        )}
                    </Row>
                );
            },
        },
        {
            title: t('Owner'),
            ariaLabel: t('Owner'),
            dataIndex: 'owner',
            key: 'owner',
            render: (_, record) => {
                const user = record.owners[0];

                return record.owners.length ? (
                    <Row wrap={false} align="middle" className="mx-0">
                        <Col flex="none">
                            <CustomAvatar name={urnMapper[user]} email={emailMapper[user]} size={24} />
                        </Col>
                        {record.owners.length > 1 && (
                            <Col flex="none">
                                <CustomTooltip
                                    items={record?.owners}
                                    itemMapper={urnMapper}
                                    emailMapper={emailMapper}
                                />
                            </Col>
                        )}
                    </Row>
                ) : (
                    <span>-</span>
                );
            },
        },
        {
            align: 'right',
            title: '',
            render: (_, record) => (
                <Row gutter={8} wrap={false} align="middle" justify="end" className="mx-0">
                    <Col flex="none">
                        <MetaDataPopup urnMapper={urnMapper} emailMapper={emailMapper} metaData={record} />
                    </Col>
                    <Col flex="none">
                        <ActionButtonContainer>
                            <Tooltip title={editDisabledTooltip} overlayClassName="f-tooltip-white">
                                <span>
                                    <Dropdown
                                        trigger={['click']}
                                        disabled={isEditDisabled}
                                        overlay={
                                            <StyledMenu>
                                                <Menu.Item
                                                    key="remove-relationship"
                                                    onClick={() => removeRelationship(record.urn)}
                                                    disabled={isSystemTerm(record) || isEditDisabled}
                                                >
                                                    {t('Remove Relationship')}
                                                </Menu.Item>
                                            </StyledMenu>
                                        }
                                    >
                                        <Button
                                            type="text"
                                            className="icon-button ma-0"
                                            icon={<MoreVertIcon />}
                                            disabled={isEditDisabled}
                                        />
                                    </Dropdown>
                                </span>
                            </Tooltip>
                        </ActionButtonContainer>
                    </Col>
                </Row>
            ),
        },
    ];

    const closeTermModal = () => {
        setShowAddModal(false);
    };

    const onChangePage = (p) => {
        setPage(p);
    };

    const onShowSizeChange = (newPage) => {
        setPageSize(newPage);
        onChangePage(1);
    };

    // Apply filters to the glossary terms based on the selected filters
    const applyFilters = (updatedFilters) => {
        if (!data?.getGlossaryTabDetails?.terms) {
            setGlossaryTerms([]);
            return;
        }

        // Get filtered terms based on search and filters
        const filteredTerms = data.getGlossaryTabDetails.terms.filter((term: any) => {
            // Check if term name matches search (if any)
            const termName = term.name?.toLowerCase() ?? '';
            const matchesSearch = searchText === '' || termName.includes(searchText.toLowerCase());

            // Skip if search doesn't match
            if (!matchesSearch) return false;

            // Check if all filters match
            return updatedFilters.every((filter) => {
                const termValues = term[filter.field]?.map((item) =>
                    typeof item === 'string' ? item.toLowerCase() : '',
                );
                return termValues?.includes(filter.values[0].toLowerCase());
            });
        });

        // Process and update state
        const processedTerms = processGlossaryTerms(filteredTerms);
        setGlossaryTerms(processedTerms || []);
        setPage(1);
    };

    const onApplyFilter = (type, urn) => {
        const filterExists = filterOptions.some((filter) => filter.field === type);

        // Update the filter options based on the selected filter type and value
        const updatedFilters = filterExists
            ? filterOptions.map((filter) => (filter.field === type ? { ...filter, values: [urn] } : filter))
            : [...filterOptions, { field: type, values: [urn] }];

        setFilterOptions(updatedFilters);
        applyFilters(updatedFilters);
    };

    const onResetFilter = (type: string) => {
        const updatedFilters = filterOptions.filter((filter) => filter.field !== type);

        setFilterOptions(updatedFilters);
        applyFilters(updatedFilters);
    };

    const getRowKey = (glossaryTerm) => glossaryTerm?.key || glossaryTerm?.name;

    const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const searchValue = e.target.value.toLowerCase();
        setSearchText(searchValue);

        if (!data?.getGlossaryTabDetails?.terms) return;

        const filteredGlossaryTerms = processGlossaryTerms(getFilteredGlossaryTerms(searchValue));

        setGlossaryTerms(filteredGlossaryTerms);
    };

    return (
        <>
            <Card>
                <Row gutter={8} className="px-3 pt-3 mx-0">
                    <Col flex="auto">
                        <Input
                            size="middle"
                            placeholder={t('Search')}
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={onSearch}
                        />
                    </Col>
                    <Col flex="none">
                        <Tooltip title={editDisabledTooltip} overlayClassName="f-tooltip-white">
                            <Button
                                type="text"
                                className="f-btn-flat"
                                onClick={() => setShowAddModal(true)}
                                disabled={isEditDisabled}
                            >
                                <PlusOutlined />
                                <span>{t('Add Term')}</span>
                            </Button>
                        </Tooltip>
                    </Col>
                </Row>
                <Space className="px-2 py-3 f-layout-wrap-xs" size="small">
                    {glossaryFilters.map((item) => (
                        <FilterDropdown
                            {...item}
                            key={item.id}
                            disabled={!data?.getGlossaryTabDetails?.terms.length}
                            onApplyFilter={onApplyFilter}
                            onResetFilter={onResetFilter}
                        />
                    ))}
                </Space>
                {loading || (isEntityLoading && <ComponentSpinner />)}
                <StyledTable
                    size="small"
                    rowKey={getRowKey}
                    loading={false}
                    pagination={false}
                    locale={{
                        emptyText: <Empty description={t('No data')} image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                    }}
                    dataSource={generateDataGrid()}
                    // typescript is complaining that default sort order is not a valid column field- overriding this here
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    columns={glossaryGird}
                />
                <Divider className="ma-0" />
                <PaginationBar
                    page={page}
                    loading={loading || false}
                    pageSize={pageSize}
                    totalCount={glossaryTerms.length}
                    onChangePage={onChangePage}
                    onShowSizeChange={onShowSizeChange}
                />
            </Card>

            {!isEditDisabled && (
                <BulkAction
                    hideExport
                    type="glossaryTermTab"
                    selectedItems={selectedItems}
                    clearSelectedItems={() => setSelectedItems([])}
                    onRemove={() => batchRemoveTerms()}
                />
            )}

            {showAddModal && (
                <EditTagTermsModal
                    type={EntityType.GlossaryTerm}
                    visible
                    onCloseModal={closeTermModal}
                    onSuccess={() => {
                        closeTermModal();
                        refetchGridListing();
                    }}
                    resources={[
                        {
                            resourceUrn: entityData?.urn || '',
                        },
                    ]}
                />
            )}
        </>
    );
};
